// © Microsoft Corporation. All rights reserved.

import {
    CommonHeaderHandledProps as BaseCommonHeaderHandledProps,
    CommonHeaderProps as BaseCommonHeaderProps,
    CommonHeaderManagedClasses,
    CommonHeaderUnhandledProps,
    ContextualNavItem
} from "./CommonHeader.props";
import manageJss, { ManagedJSSProps } from "@microsoft/fast-jss-manager-react";
import { mockDefaultState, mockState } from "./experience-info/MockState";

import { CommonHeader as BaseCommonHeader } from "./CommonHeader";
import { DesignSystem } from "@microsoft/fast-components-styles-msft";
import { ExperienceInfo } from "@msnews/core";
import { CommonHeaderActions } from "./CommonHeader.actions";
import { CommonHeaderConfig, NavigationItem, SecondaryNavigationInfo } from "./CommonHeader.config";
import { CommonHeaderConfigSchema } from "./experience-info/CommonHeader.config.schema";
import { CommonHeaderConnector, getPeregrineRouterConfig } from "./CommonHeader.connector";
import { CommonHeaderInspector } from "./CommonHeader.inspector";
import { CommonHeaderReducer } from "./CommonHeader.reducer";
import { CommonHeaderState } from "./CommonHeader.state";
import { CommonHeaderStyles } from "./CommonHeader.styles";
import { Subtract } from "utility-types";
import { mockConfig } from "./experience-info/MockConfig";
import { CommonHeaderClassNameContract } from "./CommonHeader.classnames";

// The type returned by manageJss type is very complicated so we'll let the compiler infer the type instead of re-declaring just for the package export
const CommonHeader = manageJss(CommonHeaderStyles)(BaseCommonHeader);
type CommonHeader = InstanceType<typeof CommonHeader>;

// Create a valid JSS managed interface that doesn't require managed classes which are provided by the manageJSS HOC
type CommonHeaderHandledProps = Subtract<BaseCommonHeaderHandledProps, CommonHeaderManagedClasses>;

// Create type for manageJSS HOC styled instance we are exporting
type CommonHeaderProps = ManagedJSSProps<BaseCommonHeaderProps, CommonHeaderClassNameContract, DesignSystem>;

// Define the ExperienceInfo object. This is used by tooling
const ToolingInfo: ExperienceInfo<CommonHeaderConfig, CommonHeaderState, CommonHeaderProps, never> = {
    reactComponent: CommonHeader,
    experienceConfigSchema: CommonHeaderConfigSchema,
    defaultMockState: mockDefaultState,
    mockState: mockState,
    mockConfig: mockConfig,
    inspector: CommonHeaderInspector.inspect
};

// Export everything a user might need from this index file
export {
    CommonHeader,
    CommonHeaderActions,
    CommonHeaderConfig,
    CommonHeaderConnector,
    CommonHeaderProps,
    CommonHeaderReducer,
    CommonHeaderState,
    CommonHeaderClassNameContract,
    CommonHeaderHandledProps,
    CommonHeaderUnhandledProps,
    ToolingInfo,
    getPeregrineRouterConfig,
    ContextualNavItem,
    NavigationItem,
    SecondaryNavigationInfo
};